import { create } from "zustand";
import { persist } from "zustand/middleware";

interface User {
  id: string;
  mongo_id: string;
  email: string;
  username?: string | null;
  profile_picture?: string;
  first_name?: string;
  last_name?: string;
  role_name?: string;
  plan_ids?: string[];
}

interface AuthState {
  user_uuid: string | null;
  user: User | null;
  token: string | null; // access_token
  refresh_token: string | null; // refresh_token
  expiresAt: number | null; // ms timestamp
  isAdmin: boolean;
  isManager: boolean;
  hasFetchedRole: boolean;
  hasFetchedManagerRole: boolean;
  login: (data: { user: User; token: string; refresh_token: string }) => void;
  updateUser: (updates: Partial<User>) => void;
  setAdminStatus: (isAdmin: boolean) => void;
  setManagerStatus: (isManager: boolean) => void;
  logout: () => void;
  debugExpireToken: () => void;
  // Add method to check if user logged in with hash key
  isHashKeyUser: () => boolean;
}

function decodeJwt(token: string) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user_uuid: null,
      user: null,
      token: null,
      refresh_token: null,
      expiresAt: null,
      isAdmin: false,
      isManager: false,
      hasFetchedRole: false,
      hasFetchedManagerRole: false,

      login: ({ user, token, refresh_token }) => {
        const decoded = decodeJwt(token);
        const expMs = decoded?.exp ? decoded.exp * 1000 : null;

        set({
          user_uuid: user.id,
          user,
          token: token,
          refresh_token: refresh_token,
          expiresAt: expMs,
          // Don't reset admin status if it's already been fetched
          // This prevents the brief login form flash during token refresh
          isAdmin: get().hasFetchedRole ? get().isAdmin : false,
          isManager: get().hasFetchedManagerRole ? get().isManager : false,
          hasFetchedRole: get().hasFetchedRole,
          hasFetchedManagerRole: get().hasFetchedManagerRole,
        });
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              ...updates,
            },
          });
        }
      },

      setAdminStatus: (isAdmin: boolean) => {
        set({
          isAdmin,
          hasFetchedRole: true,
        });
      },

      setManagerStatus: (isManager: boolean) => {
        set({
          isManager,
          hasFetchedManagerRole: true,
        });
      },

      // Add method to detect hash key authentication
      isHashKeyUser: () => {
        const token = get().token;
        if (!token) return false;

        const decoded = decodeJwt(token);
        return decoded?.type === "hash_key";
      },

      logout: () => {
        set({
          user_uuid: null,
          user: null,
          token: null,
          refresh_token: null,
          expiresAt: null,
          isAdmin: false,
          isManager: false,
          hasFetchedRole: false,
          hasFetchedManagerRole: false,
        });
        localStorage.removeItem("auth-storage");
      },

      debugExpireToken: () => {
        set({ expiresAt: Date.now() - 1000 });
      },
    }),
    {
      name: "auth-storage",
      // Add onRehydrateStorage to fix expiresAt on app startup
      onRehydrateStorage: () => (state) => {
        if (
          state?.token &&
          state?.user &&
          state?.refresh_token &&
          !state?.expiresAt
        ) {
          // Fix missing expiresAt on rehydration
          const decoded = decodeJwt(state.token);
          const expMs = decoded?.exp ? decoded.exp * 1000 : null;
          if (expMs) {
            state.expiresAt = expMs;
          }
        }
      },
    },
  ),
);
